import {Skeleton} from 'antd';
import React from 'react';

import {ReactComponent as AIInsightSparkle} from '../../../../assets/icons/AIInsightSparkle.svg';
import type {OverviewInsightResult} from '../../../../services/modules/ClimateDB/overviewInsightAPI';

import {
  numberFormatter,
  SCOPE_COLORS,
  SCOPE_SERIES,
  type ScopeKey
} from './SustainabilityChartParts';

interface SustainabilityInsightPanelProps {
  status: 'loading' | 'ready' | 'hidden';
  result: OverviewInsightResult | null;
  scopeValues?: Record<ScopeKey, number>;
  showMetric?: boolean;
  showShareBar?: boolean;
  formatValue?: (value: number) => string;
}

const TITLE = 'AI-generated insight';

const scopeFromLabel = (label: string): ScopeKey | null => {
  const match = /^scope\s*([123])\b/i.exec(label.trim());

  return match ? (`scope${match[1]}` as ScopeKey) : null;
};

const ScopeShareBar: React.FC<{values: Record<ScopeKey, number>}> = ({
  values
}) => {
  const segments = SCOPE_SERIES.map((series) => ({
    key: series.key,
    color: series.color,
    value: values[series.key]
  }))
    .filter((segment) => segment.value > 0)
    .sort((a, b) => b.value - a.value);

  if (!segments.length) {
    return null;
  }

  return (
    <div className="yoyPerformanceInsightShare" aria-hidden="true">
      {segments.map((segment) => (
        <i
          key={segment.key}
          style={{backgroundColor: segment.color, flexGrow: segment.value}}
        />
      ))}
    </div>
  );
};

const SustainabilityInsightPanel: React.FC<SustainabilityInsightPanelProps> = ({
  status,
  result,
  scopeValues,
  showMetric = false,
  showShareBar = false,
  formatValue = (value: number) => numberFormatter.format(value)
}) => {
  const ready = result?.state === 'ready' ? result : null;

  let body: React.ReactNode;

  if (status === 'loading') {
    body = <Skeleton active paragraph={{rows: 4}} title={false} />;
  } else if (!ready) {
    body = (
      <p className="yoyPerformanceInsightEmpty">
        AI insight is not available for this year.
      </p>
    );
  } else {
    body = (
      <>
        <p className="yoyPerformanceInsightSummary">{ready.summary}</p>

        {showShareBar && scopeValues ? (
          <ScopeShareBar values={scopeValues} />
        ) : null}

        {ready.factors.length > 0 && (
          <div className="yoyPerformanceInsightFactors">
            {ready.factors.map((factor) => {
              const scope = scopeFromLabel(factor.name);
              const value =
                scope && scopeValues ? scopeValues[scope] : undefined;

              return (
                <div className="yoyPerformanceInsightFactor" key={factor.name}>
                  <div className="yoyPerformanceInsightFactorInfo">
                    <span
                      className="yoyPerformanceInsightPill"
                      style={{
                        backgroundColor: scope
                          ? SCOPE_COLORS[scope]
                          : SCOPE_COLORS.scope1
                      }}
                    >
                      <i />
                      {factor.name}
                    </span>
                    <span className="yoyPerformanceInsightFactorDetail">
                      {factor.detail}
                    </span>
                    {showMetric && factor.value ? (
                      <span className="yoyPerformanceInsightFactorMetric">
                        {factor.value}
                      </span>
                    ) : null}
                  </div>
                  {value !== undefined ? (
                    <div className="yoyPerformanceInsightFactorValue">
                      <strong>{formatValue(value)}</strong>
                      <span>tCO2e</span>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        {ready.suggestion ? (
          <p className="yoyPerformanceInsightSummary">{ready.suggestion}</p>
        ) : null}
      </>
    );
  }

  return (
    <aside
      className="yoyPerformanceInsight light-scroll"
      aria-label={TITLE}
      aria-busy={status === 'loading'}
    >
      <div className="yoyPerformanceInsightHeader">
        <span className="yoyPerformanceInsightMark">
          <AIInsightSparkle aria-hidden="true" />
        </span>
        <span>{TITLE}</span>
      </div>
      {body}
    </aside>
  );
};

export default SustainabilityInsightPanel;
